"use client";

import {
  ImpromptuSettings,
  type ImpromptuSettingsProps,
} from "@/components/impromptu/ImpromptuSettings";
import { PromptStudio } from "@/components/impromptu/PromptStudio";
import type {
  ImpromptuLanguageCode,
  ImpromptuLearnerProfile,
  ImpromptuPrompt,
  ImpromptuPromptSelection,
} from "@/lib/impromptu/config";
import { IMPROMPTU_COPY } from "@/lib/impromptu/copy";

/**
 * The setup surface for the embedded mode, where impromptu is one option among
 * several and the learner has already chosen it.
 *
 * Deliberately not the landing page: the marketing sections that used to render
 * here were behind `!embedded` anyway, so this branch was already showing a hero
 * with nothing under it. Someone who has picked a mode needs the prompt and the
 * three settings, not a pitch for the thing they are already using.
 */

const COPY = IMPROMPTU_COPY.setup;

type ImpromptuSetupPanelProps = {
  languageCode: ImpromptuLanguageCode;
  learnerProfile: ImpromptuLearnerProfile;
  prompt: ImpromptuPromptSelection;
  prompts: readonly ImpromptuPrompt[];
  settings: Omit<ImpromptuSettingsProps, "surface" | "className" | "disabled">;
  onPromptChange: (prompt: ImpromptuPromptSelection) => void;
  onStart: () => void;
};

export function ImpromptuSetupPanel({
  languageCode,
  learnerProfile,
  prompt,
  prompts,
  settings,
  onPromptChange,
  onStart,
}: ImpromptuSetupPanelProps) {
  return (
    <div className="mx-auto w-full max-w-6xl">
      <div className="grid items-start gap-10 py-3 lg:grid-cols-[minmax(0,0.88fr)_minmax(32rem,1.12fr)] lg:gap-16">
        <section className="min-w-0">
          {/* No eyebrow: the shell header two rows above already reads
              "Impromptu practice", and repeating it here spent the most
              prominent line on the surface saying where you already are. */}
          <h1 className="max-w-2xl text-4xl font-extrabold leading-[1.08] text-white sm:text-6xl">
            {COPY.heading}
          </h1>
          <p className="mt-5 max-w-xl text-base leading-7 text-[#AEAEB2] sm:text-lg">
            {COPY.subheading}
          </p>

          <ImpromptuSettings {...settings} surface="dark" className="mt-8" />

          <p className="mt-5 max-w-lg text-xs leading-5 text-[#8E8E93]">{IMPROMPTU_COPY.privacy}</p>
        </section>

        <PromptStudio
          prompt={prompt}
          prompts={prompts}
          languageCode={languageCode}
          learnerProfile={learnerProfile}
          onPromptChange={onPromptChange}
          onStart={onStart}
        />
      </div>
    </div>
  );
}
